import { FILTER_CHIP_TRIGGER } from '~/components/new/app-ui/filter-chip-styles'
import { cn } from '~/lib/utils'

/** Classes des écrans Logistique : boutons des maquettes, filtres, menus de filtre. */

/** Boutons des maquettes (en-têtes de liste et de fiche) : action principale, action secondaire. */
export const LOGISTICS_PRIMARY_BUTTON_CLASS =
  'h-[42px] rounded-lg bg-primary-600 text-sm leading-4 font-semibold text-sidebar hover:bg-primary-700'
export const LOGISTICS_SECONDARY_BUTTON_CLASS =
  'h-[42px] rounded-lg bg-background-100 text-sm leading-4 font-semibold text-primary-600 hover:bg-primary-50'

/**
 * États communs d'un choix en pastille (jours d'une tournée, statut et permis d'un chauffeur) :
 * texte vert, anneau au focus, grisé désactivé (`disabled` d'un bouton, `data-disabled` d'un
 * `Radio`). Chaque pastille garde sa taille, sa forme et ses couleurs une fois retenue.
 */
export const LOGISTICS_CHOICE_BASE_CLASS =
  'text-primary-600 outline-none transition-colors focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:cursor-not-allowed disabled:opacity-60 data-disabled:cursor-not-allowed data-disabled:opacity-60'

/** Pastille vert foncé une fois retenue (statut d'un chauffeur) ; les permis gardent la base seule. */
export const LOGISTICS_CHOICE_STATE_CLASS = cn(
  LOGISTICS_CHOICE_BASE_CLASS,
  'data-checked:bg-primary-600 data-checked:text-sidebar data-checked:hover:bg-primary-600',
)

/** Chip des filtres de la barre Logistique (cuisines, départements, période) : un seul style. */
export const LOGISTICS_FILTER_CHIP = cn(
  FILTER_CHIP_TRIGGER,
  'border-0 bg-sidebar p-2 hover:bg-background-100',
)

/**
 * Filtres d'une liste de référentiel (maquettes « Camions », « Hubs ») : même base, à la surface du bouton
 * secondaire des maquettes (pastille beige de 42 px au texte vert).
 */
export const LOGISTICS_LIST_FILTER_CHIP = cn(
  FILTER_CHIP_TRIGGER,
  LOGISTICS_SECONDARY_BUTTON_CLASS,
  // `data-[size=default]` : la hauteur du `SelectTrigger` (h-9) l'emporte sinon sur celle du bouton.
  // `font-sans` : le texte des maquettes est en Montserrat, pas dans la police de marque des chips.
  'gap-1.5 border-0 px-3 font-sans data-placeholder:text-primary-600 data-[size=default]:h-[42px] [&_svg]:text-primary-600',
)

/**
 * Filtre d'une carte (maquette « Select » md, filtres du panneau « Tournées simulées ») : icône de
 * 24 px, libellé et chevron collés à gauche, sur le fond beige des contrôles. Le libellé se tronque
 * quand la place manque.
 */
export const LOGISTICS_PANEL_FILTER =
  'h-9 w-full min-w-0 justify-start gap-1.5 rounded-lg border-0 bg-sidebar px-3 py-1.5 font-sans text-sm leading-4 font-semibold text-primary-600 shadow-none hover:bg-background-100 hover:text-primary-600 [&_svg]:text-primary-600'

/** Icône d'un filtre de carte : 24 px, au trait des Hugeicons des maquettes. */
export const LOGISTICS_PANEL_FILTER_ICON = 'size-6 shrink-0'

/**
 * Message d'erreur (`errorClassName` de `Field`) de la dernière des trois colonnes d'heures d'une
 * tournée (départ, limite, retour) : il s'étale sur les trois colonnes, soit deux colonnes et leurs
 * deux gouttières vers la gauche, au lieu de s'empiler sur la largeur d'une seule. Le refus
 * départ/retour (#903) s'y pose, et sa phrase nomme l'autre choix de jour d'arrivée. Suppose une
 * ligne `grid-cols-3 gap-2` ou trois `flex-1` en `gap-2` : à revoir si la ligne change.
 */
export const LOGISTICS_TIME_ROW_LAST_ERROR_CLASS = '-ml-[calc(200%+1rem)]'

/**
 * Pas des minutes des horaires logistiques (`TimePicker` des hubs et de la création de tournée).
 * 23:55 reste atteignable : le `TimePicker` réserve la minute 59 à 23 h (fin de journée), hors du
 * pas.
 */
export const LOGISTICS_TIME_MINUTES_STEP = 5

/**
 * Barre Logistique sur une ligne (maquette `12167:43322`) dès que son conteneur mesure 73,5rem
 * (1176 px, `--container-logistics-bar` du thème). Au-dessus du seuil, les onglets défilent (fondu
 * du côté coupé, barre au survol) plutôt que de pousser les filtres dessous quand la place manque :
 * valeurs de filtre plus longues que les libellés (chips bornés à 15rem chacun).
 */
export const LOGISTICS_NAV_BAR_ONE_LINE = '@min-logistics-bar:flex-nowrap'
export const LOGISTICS_NAV_FILTERS_ONE_LINE =
  '@min-logistics-bar:shrink-0 @min-logistics-bar:flex-nowrap'

/** Zone de défilement des onglets de la barre : à la largeur des onglets, bornée par la barre. */
export const LOGISTICS_NAV_TABS_SCROLL = 'size-auto max-w-full min-w-0'

/** Menu d'un filtre : au moins aussi large que le filtre, élargi à ses libellés. */
export const LOGISTICS_SELECT_CONTENT_CLASS = 'w-auto min-w-(--anchor-width)'

/** Étiquette d'une liste de référentiel (type et rattachement d'un camion, hub fictif). */
export const LOGISTICS_TAG_CLASS = 'h-auto rounded-[7px] px-2.5 py-1 text-[11px] font-bold'

/** En-têtes de colonne d'une liste de référentiel (maquettes « Camions », « Hubs »). */
export const LOGISTICS_TABLE_HEADER_CLASS =
  'text-xs leading-[14px] font-semibold text-sage-400 uppercase'

/**
 * Tableau d'une liste de référentiel : en-tête gris-vert, lignes de 59 px sur fond clair, cellules à
 * 12 px.
 */
export const LOGISTICS_TABLE_CLASS =
  '[&_thead_tr]:bg-sage-100 [&_th]:h-auto [&_th]:p-3 [&_td]:p-3 [&_tbody_tr]:h-[59px] [&_tbody_tr]:bg-sage-50 [&_tbody_tr:hover]:bg-primary-50/60 [&_tr]:border-black/6'

/** Cadre de ce tableau : coins de 8 px, sans bordure visible (maquettes). */
export const LOGISTICS_TABLE_WRAPPER_CLASS = 'rounded-lg border-transparent'

/** Interrupteur Actif / Inactif des maquettes : piste vert pâle et pastille verte, gris éteint. */
export const LOGISTICS_STATUS_SWITCH_CLASS =
  'data-checked:bg-primary-200 data-unchecked:bg-sage-300 [&_[data-slot=switch-thumb]]:data-checked:bg-primary-500 [&_[data-slot=switch-thumb]]:data-unchecked:bg-sage-500'

/** Libellé d'en-tête de fiche, au-dessus d'une valeur ou d'un interrupteur (« Statut du profil »). */
export const LOGISTICS_HEADER_LABEL_CLASS = 'text-xs leading-[14px] font-semibold text-sage-500'

/** Fond des champs d'une fiche : beige. */
export const LOGISTICS_FIELD_BACKGROUND_CLASS = 'bg-background-100'

/** Surface des champs d'une fiche (maquettes « Identité ») : 38 px, beige, sans bordure. */
export const LOGISTICS_FIELD_SURFACE_CLASS = `h-[38px] rounded-[10px] border-0 ${LOGISTICS_FIELD_BACKGROUND_CLASS} shadow-none`

/** Texte saisi dans un champ de fiche (texte, téléphone), sur sa surface. */
export const LOGISTICS_FIELD_INPUT_CLASS =
  'px-3.5 text-sm font-medium text-primary-900 placeholder:text-primary-700/40'

/** Libellé 10 px d'un champ de fiche, au-dessus du champ. */
export const LOGISTICS_FIELD_LABEL_CLASS = 'text-[10px] leading-3 font-medium text-primary-700'

/** Sélecteur d'une fiche : même surface que les champs texte. */
export const LOGISTICS_FIELD_SELECT_CLASS = cn(
  LOGISTICS_FIELD_SURFACE_CLASS,
  // Le déclencheur fixe sa hauteur par taille (`data-[size=default]:h-9`) : 38 px comme les saisies.
  'w-full data-[size=default]:h-[38px] px-3.5 text-sm font-medium text-primary-700 data-placeholder:text-primary-700/60',
)

/**
 * Menu d'un sélecteur de fiche à sections (site de départ : ateliers, hubs), au style du
 * `StoreSelector` du DS (lignes `SelectableRow`) en choix unique : panneau crème, intitulés de
 * section en gras avec leur nombre à droite, options en retrait, survol vert pâle, coche verte.
 */
export const LOGISTICS_SECTION_MENU = {
  // Les sections n'ajoutent pas de marge à celle du panneau (lignes espacées de 2 px, DS), et les
  // flèches de défilement prennent son fond.
  contentClassName:
    'rounded-2xl border border-background-200 bg-sidebar p-3 shadow-none ring-0 [&_[data-slot=select-group]]:p-0 [&_[data-slot=select-scroll-down-button]]:bg-sidebar [&_[data-slot=select-scroll-up-button]]:bg-sidebar',
  groupLabelClassName:
    'mb-0.5 flex h-9 items-center gap-2 px-2 py-1.5 text-sm leading-4 font-semibold text-primary-700 [&_[data-slot=select-label-meta]]:ml-auto [&_[data-slot=select-label-meta]]:text-xs [&_[data-slot=select-label-meta]]:leading-[14px] [&_[data-slot=select-label-meta]]:font-medium [&_[data-slot=select-label-meta]]:text-sage-500',
  itemClassName:
    'mb-0.5 h-9 rounded-lg px-8 py-1.5 text-sm leading-4 font-medium text-sage-700 focus:bg-primary-50 focus:text-sage-700 not-data-[variant=destructive]:focus:**:text-sage-700 [&_svg]:text-primary-600 not-data-[variant=destructive]:focus:[&_svg]:text-primary-600',
} as const
